/**
 * The shell every route renders inside, and the four gates in front of it.
 *
 * Both gates live here rather than in a `beforeLoad` on each route, and for the
 * same reason: they have a "not known yet" state that must render as the app's
 * own skeleton. A redirect fired during that window signs the user out visually
 * on every cold start, which on an installed PWA is the first thing they see
 * every morning.
 *
 *   auth unknown      skeleton
 *   signed out        the sign-in screen
 *   tenancy unknown   skeleton
 *   no tenant         onboarding — accept an invitation, or start a kennel
 *   otherwise         the app
 */
import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  Link,
  Outlet,
  useRouterState,
} from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/auth/AuthProvider.tsx';
import { useTenantDoc } from '@/api/tenant.ts';
import { Avatar } from '@/components/Avatar.tsx';
import { CollarStrip, Mark, Wordmark } from '@/components/Brand.tsx';
import { SyncStatus } from '@/components/SyncStatus.tsx';
import { UpdateBar } from '@/components/UpdateBar.tsx';
import { SignIn } from '@/components/SignIn.tsx';
import { useServiceWorker } from '@/offline/useServiceWorker.ts';
import { Onboarding } from '@/tenant/Onboarding.tsx';
import { useProfile, useTenant } from '@/tenant/TenantProvider.tsx';

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootLayout,
});

function RootLayout() {
  const { status } = useAuth();
  const { tenantId, loading } = useTenant();

  /**
   * The one route that is not behind the gate below.
   *
   * `/join/<inviteId>` is the address in an invitation email, and the person
   * opening it has no session and belongs to no tenant — which is exactly what
   * every branch below treats as "show them the sign-in screen instead". Doing
   * that here would swallow the invitation id, which is the only thing the link
   * carried, and leave a stranger looking at a sign-in prompt with no idea why.
   *
   * So the route draws its own page and its own three auth states. Matched on the
   * pathname rather than on a route id, because this decision happens above the
   * router's own matching. See routes/join.$inviteId.tsx.
   */
  const joining = useRouterState({
    select: (state) => state.location.pathname.startsWith('/join/'),
  });

  /**
   * Calling this hook is what registers the service worker, so it is called
   * ABOVE the gates and not inside the signed-in branch.
   *
   * The worker is the offline shell — the thing that makes this an app rather
   * than a website with an icon. Registering it only after sign-in meant a
   * person who installed it to their home screen and did not sign in that
   * minute had no shell at all, and the offline e2e spec, which loads the app
   * signed out, waited for a worker that was never going to arrive.
   */
  const worker = useServiceWorker();

  return (
    <>
      <UpdateBar worker={worker} />
      {joining ? (
        <Outlet />
      ) : status === 'loading' || loading ? (
        <Skeleton />
      ) : status === 'signed-out' ? (
        <SignIn />
      ) : tenantId === null ? (
        <Onboarding />
      ) : (
        <div className="flex min-h-dvh flex-col">
          <Header tenantId={tenantId} />
          <main className="mx-auto w-full max-w-2xl flex-1 px-4 py-5">
            <Outlet />
          </main>
        </div>
      )}
    </>
  );
}

/**
 * The mark, the tenant, and who you are.
 *
 * The brand is here because an installed PWA has no address bar and no tab
 * title: without it the app is a page of grey text with no name on it, and the
 * icon the user tapped on their home screen never appears again. It is also the
 * way home, which is why it is a `Link` and not a heading — the offspring list
 * is the app's one root, and every other screen is a place you came from it.
 *
 * The tenant sits BESIDE the wordmark and larger than it, and that ranking is
 * the point: the product is the thing you opened, the kennel is the thing you are
 * writing into. A breeder knows which app they opened; what they cannot know
 * without being told is which kennel this is — and once a vet belongs to two,
 * that is the difference between a weight recorded and a weight recorded in the
 * wrong place. So the kennel gets the size and "Puppy Doctor" gets what is left.
 *
 * The person and their role are at the other end, against the avatar, because
 * they answer a different question from the tenant: not "where am I writing" but
 * "as whom". On a shared tablet that is the question, and the role under the name
 * is what tells a viewer who cannot find the save button why not.
 */
function Header({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { role } = useTenant();
  const profile = useProfile();
  const { data: tenant } = useTenantDoc(tenantId);

  return (
    <header className="sticky top-0 z-10 border-b border-line bg-page/90 backdrop-blur">
      <div className="mx-auto flex w-full max-w-2xl items-center gap-2 px-4 py-2">
        <Link
          to="/"
          className="flex min-h-tap min-w-0 flex-1 items-center gap-1.5 rounded-xl pr-1"
        >
          <Mark className="size-8 shrink-0 self-center" />
          {/* Both lines, at every width. The wordmark is the one below `sm`
              that is easiest to cut — it is the mark the user already tapped on
              the home screen to get here — but a header that only sometimes
              carries the product name reads as unfinished, and the person block
              on the other end is already capped rather than shrinkable, so the
              two lines here truncating is enough to keep the row from
              overflowing on a narrow phone. */}
          <span className="flex min-w-0 flex-col justify-center gap-0">
            <Wordmark className="truncate text-xs leading-none" />
            {tenant?.name !== undefined && (
              <span className="min-w-0 truncate text-lg leading-none font-bold tracking-[-0.02em]">
                {tenant.name}
              </span>
            )}
          </span>
        </Link>

        <SyncStatus />

        {/* The way to the people screen, and to sign-out and settings with it.
            An avatar rather than the icon that used to be here: it is the one
            control on a shared tablet that answers "whose session is this",
            which the previous glyph could not.

            The border stays transparent even on the active route — the ring
            around the avatar on the people screen itself read as chrome around
            chrome, since the page beneath it already says where the user is.

            The name is text-right and the role under it, both truncating, and
            the block is CAPPED rather than merely shrinkable. A display name is
            whatever somebody typed, and an uncapped one sets the width of this
            side from its own content — which is how "Małgorzata
            Kowalska-Nowakowska" would take the kennel name's half of the header
            with it. Capped, a long name loses its own tail and nothing else. */}
        <Link
          to="/members"
          aria-label={t('member.title')}
          className="flex min-h-tap min-w-0 shrink items-center gap-2 rounded-full border-2 border-transparent pl-2"
        >
          <span className="min-w-0 max-w-32 text-right">
            <span className="block truncate text-sm leading-tight font-medium">{profile.name}</span>
            {role !== null && (
              <span className="block truncate text-xs leading-tight text-ink-faint">
                {t(`role.${role}`)}
              </span>
            )}
          </span>
          <Avatar name={profile.name} email={user?.email ?? null} />
        </Link>
      </div>
    </header>
  );
}

/**
 * What "not known yet" looks like. Deliberately the app's own furniture and not
 * a spinner: the answer arrives in a few hundred milliseconds from IndexedDB,
 * and a spinner in that window reads as a stall where a quiet frame reads as a
 * page still painting.
 */
function Skeleton() {
  return (
    <div className="grid min-h-dvh place-items-center px-6">
      <CollarStrip className="opacity-40" />
    </div>
  );
}
